<template>
  <div class="container-wrapper">
    <div class="grid-container-wrapper">
      <SelectInput
        label="資格・免許名"
        v-model="form.licenseType"
        :options="licenseOptions"
        placeholder="選択してください"
        :isRequired="true"
        :disabled="!!license"
      ></SelectInput>
      <p
        v-if="validationErrors.licenseType"
        class="form-error grid-column-start grid-full-width"
      >
        {{ validationErrors.licenseType }}
      </p>
      <TextInput
        label="番号"
        placeholder="例) 152732"
        :isRequired="false"
        v-model="form.number"
        :errorMessage="validationErrors.number"
      />
      <DateInput
        label="取得日（更新日）"
        placeholder="---- / -- / --"
        :isRequired="true"
        :disableFutureDates="true"
        class="grid-column-start"
        v-model="form.issueDate"
        :errorMessage="validationErrors.issueDate"
      />
      <DateInput
        label="次回更新期限"
        placeholder="---- / -- / --"
        :isRequired="true"
        v-model="form.renewalDate"
        :errorMessage="validationErrors.renewalDate"
        v-if="showRenewalDate"
      />
      <ImageInput
        :maxSizeMB="5"
        :isRequired="false"
        class="grid-column-start grid-full-width"
        v-model="form.attachments"
        @change="handleImageInputChange"
      />
      <p
        v-if="validationErrors.attachments"
        class="form-error grid-column-start grid-full-width"
      >
        {{ validationErrors.attachments }}
      </p>
      <LongTextInput
        label="備考"
        placeholder="備考を入力してください"
        class="grid-column-start grid-full-width"
        v-model="form.remarks"
        :errorMessage="validationErrors.remarks"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SelectInput from "../../../components/form/SelectInput.vue";
import TextInput from "../../../components/form/TextInput.vue";
import DateInput from "../../../components/form/DateInput.vue";
import ImageInput from "../../../components/form/ImageInput.vue";
import LongTextInput from "../../../components/form/LongTextInput.vue";

export default {
  name: "OtherForm",
  components: {
    SelectInput,
    TextInput,
    DateInput,
    ImageInput,
    LongTextInput,
  },
  computed: {
    ...mapGetters("crewSettingsLicense", ["getLicenseTermYearsById"]),
    showRenewalDate() {
      return (
        this.currentLicenseTermYears !== null &&
        this.currentLicenseTermYears !== undefined
      );
    },
  },
  props: {
    license: {
      type: String,
      default: "",
    },
    licenseOptions: {
      type: Array,
      default: () => [],
    },
    initialFields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      form: this.buildInitialFormState(),
      validationErrors: {},
      isHydrating: false,
      imageChangeData: null, // ImageInputからの変更データを保持
      currentLicenseTermYears: null, // 現在選択されている資格の有効期限
    };
  },

  watch: {
    /** ライセンスオプション変更時、デフォルト値を設定 */
    licenseOptions: {
      handler(newOptions) {
        if (!this.form.licenseType && newOptions.length > 0) {
          this.form.licenseType = newOptions[0].id;
        } else {
          this.updateCurrentLicenseTermYears(this.form.licenseType);
        }
        this.emitLicenseTypeChange();
      },
      immediate: true,
    },

    /** ライセンスプロップ変更時、フォームタイプを更新 */
    license(newValue) {
      if (newValue) {
        this.form.licenseType = newValue;
      }
    },

    /** ライセンスタイプ変更時に有効期限情報を更新 */
    "form.licenseType": {
      handler(newLicenseType) {
        this.updateCurrentLicenseTermYears(newLicenseType);
        this.emitLicenseTypeChange();
      },
      immediate: true,
    },

    /** 取得日変更時、次回更新期限を自動計算 */
    "form.issueDate"(newIssueDate) {
      if (this.isHydrating || !this.showRenewalDate) {
        return;
      }
      if (newIssueDate && this.currentLicenseTermYears) {
        this.calculateRenewalDate(newIssueDate);
      }
    },

    /** 初期値変更時にフォームへ適用 */
    initialFields: {
      handler() {
        this.applyInitialFields();
      },
      deep: true,
    },
  },

  methods: {
    /** 初期値を考慮したフォームデータを生成 */
    buildInitialFormState() {
      return {
        ...this.createDefaultForm(),
        ...this.extractInitialFields(this.initialFields),
      };
    },

    /** 初期値から入力対象フィールドのみ抽出 */
    extractInitialFields(fields = {}) {
      const allowed = [
        "licenseType",
        "number",
        "issueDate",
        "renewalDate",
        "attachments",
        "remarks",
      ];
      return allowed.reduce((acc, key) => {
        if (fields[key] !== undefined && fields[key] !== null) {
          acc[key] = fields[key];
        }
        return acc;
      }, {});
    },

    /** 初期値をフォームへ反映 */
    applyInitialFields() {
      this.isHydrating = true;
      this.form = this.buildInitialFormState();
      this.validationErrors = {};
      this.emitLicenseTypeChange();
      this.updateCurrentLicenseTermYears(this.form.licenseType);
      this.$nextTick(() => {
        this.isHydrating = false;
      });
    },

    /** デフォルトのフォーム値を作成 */
    createDefaultForm() {
      return {
        licenseType: this.license || "",
        number: "",
        issueDate: "",
        renewalDate: "",
        attachments: [],
        remarks: "",
      };
    },

    /** 親コンポーネントへライセンスタイプの変更を通知 */
    emitLicenseTypeChange() {
      const value = this.form.licenseType;
      const option = this.licenseOptions.find(
        option => option && option.id === value
      );
      this.$emit("license-type-change", option.label);
    },

    /** フォームをリセット、デフォルト値を再適用 */
    resetForm() {
      this.applyInitialFields();
      if (!this.form.licenseType && this.licenseOptions.length > 0) {
        this.form.licenseType = this.licenseOptions[0].id;
        this.emitLicenseTypeChange();
      }
    },

    /**
     * ImageInputからの変更を受け取る
     * @param {Object} changeData - {uploadedFiles, newFiles, removedFileIds}
     */
    handleImageInputChange(changeData) {
      this.imageChangeData = changeData;
    },

    /** 現在のライセンスタイプに応じた有効期限年数を更新 */
    updateCurrentLicenseTermYears(licenseType) {
      const hasLicenseType =
        licenseType !== undefined && licenseType !== null && licenseType !== "";
      const termYears = hasLicenseType
        ? this.getLicenseTermYearsById(licenseType)
        : null;
      this.currentLicenseTermYears = termYears;

      if (!this.showRenewalDate) {
        this.form.renewalDate = "";
        this.$delete(this.validationErrors, "renewalDate");
        return;
      }

      if (
        this.form.issueDate &&
        this.currentLicenseTermYears &&
        !this.isHydrating
      ) {
        this.calculateRenewalDate(this.form.issueDate);
      }
    },

    /** 取得日から次回更新期限を計算 */
    calculateRenewalDate(issueDate) {
      if (!issueDate || !this.currentLicenseTermYears) return;

      const renewalDate = new Date(issueDate);
      renewalDate.setFullYear(
        renewalDate.getFullYear() + this.currentLicenseTermYears
      );

      // YYYY-MM-DD形式に変換
      const year = renewalDate.getFullYear();
      const month = String(renewalDate.getMonth() + 1).padStart(2, "0");
      const day = String(renewalDate.getDate()).padStart(2, "0");

      this.form.renewalDate = `${year}-${month}-${day}`;
    },

    /** フォームを検証して妥当性を返す */
    validate() {
      this.validationErrors = {};

      // ライセンスタイプチェック
      if (!this.form.licenseType) {
        this.$set(
          this.validationErrors,
          "licenseType",
          "資格・免許名を選択してください"
        );
      }

      if (this.form.number && this.form.number.length > 32) {
        this.$set(
          this.validationErrors,
          "number",
          "番号は32文字以内で入力してください"
        );
      }

      // 取得日（更新日）チェック（必須）
      if (!this.form.issueDate) {
        this.$set(
          this.validationErrors,
          "issueDate",
          "取得日（更新日）を入力してください"
        );
      }

      // 取得日（最終更新日）チェック（必須）
      if (this.showRenewalDate && !this.form.renewalDate) {
        this.$set(
          this.validationErrors,
          "renewalDate",
          "取得日（最終更新日）を入力してください"
        );
      }

      // 取得日と次回更新期限の日付順序チェック
      if (
        this.showRenewalDate &&
        this.form.renewalDate &&
        this.form.issueDate
      ) {
        const issueDate = new Date(this.form.issueDate);
        const renewalDate = new Date(this.form.renewalDate);
        // 年月日だけに正規化（時分秒・ms を切り捨て）
        issueDate.setHours(0, 0, 0, 0);
        renewalDate.setHours(0, 0, 0, 0);
        if (issueDate >= renewalDate) {
          this.$set(
            this.validationErrors,
            "renewalDate",
            "次回更新期限は取得日より後の日付を入力してください"
          );
        }
      }

      if (this.form.remarks && this.form.remarks.length > 255) {
        this.$set(
          this.validationErrors,
          "remarks",
          "備考は255文字以内で入力してください"
        );
      }

      // 番号、次回更新期限、画像、備考は任意入力のためチェックなし

      return Object.keys(this.validationErrors).length === 0;
    },

    /** フォームデータをペイロード形式で返す */
    getPayload() {
      const files = [];
      const removeFileIds = this.imageChangeData
        ? this.imageChangeData.removedFileIds
        : [];

      // ImageInputから新規ファイルを取得
      if (this.imageChangeData && this.imageChangeData.newFiles) {
        this.imageChangeData.newFiles.forEach(fileObj => {
          if (fileObj.file instanceof File) {
            files.push(fileObj.file);
          }
        });
      }

      // APIペイロード用の基本情報
      const payload = {
        enterprise_crew_license_id: this.form.licenseType,
        number: this.form.number,
        expire_date:
          this.showRenewalDate && this.form.renewalDate
            ? this.form.renewalDate
            : null,
        start_date: this.form.issueDate,
        remarks: this.form.remarks || "",
        remove_enterprise_crew_license_file_id:
          removeFileIds.length > 0 ? removeFileIds : [],
      };

      // Fileオブジェクトをpayloadに含める
      payload.files = files;

      return payload;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.container-wrapper {
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  .grid-container-wrapper {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(240px, 1fr));
    gap: 24px;
    flex-wrap: wrap;
    .grid-column-start {
      grid-column-start: 1;
    }
    .grid-full-width {
      grid-column: 1 / -1;
    }
    .form-error {
      color: $aisea-red;
      font-size: 12px;
      line-height: 1.5;
      margin-top: -4px;
      margin-bottom: 4px;
    }
  }
}
</style>
